import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';
import { Award, Users, Shield, Leaf } from 'lucide-react';

export const About = () => {
  const { t } = useLanguage();

  const values = [
    { key: 'quality', icon: Award, color: 'bg-green-100 text-green-600' },
    { key: 'expertise', icon: Users, color: 'bg-sky-100 text-sky-600' },
    { key: 'trust', icon: Shield, color: 'bg-orange-100 text-orange-600' },
    { key: 'eco', icon: Leaf, color: 'bg-emerald-100 text-emerald-600' },
  ];

  const certifications = [
    'RGE QualiPV',
    'Qualit\'EnR',
    'EDF OA',
    'ENEDIS',
  ];

  return (
    <section id="about" className="py-20 md:py-32 bg-stone-50" data-testid="about-section">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Image & Stats */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?auto=format&fit=crop&q=80&w=1000"
                alt="Team working on solar installation"
                className="w-full aspect-[4/3] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-green-900/40 to-transparent" />
            </div>

            {/* Experience Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-6 border border-stone-100"
            >
              <p className="text-4xl font-bold text-green-700">10+</p>
              <p className="text-stone-600 font-medium">{t('about.imageSubTitle')}</p>
            </motion.div>

            {/* Trust Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="absolute -top-4 -left-4 bg-green-700 text-white rounded-2xl shadow-xl px-5 py-3"
            >
              <p className="font-bold">500+ Clients</p>
              <p className="text-sm text-green-100">{t('about.imageTitle')}</p>
            </motion.div>
          </motion.div>

          {/* Right - Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-outfit text-4xl md:text-5xl font-semibold tracking-tight text-stone-900 mb-4">
              {t('about.title')}
            </h2>
            <p className="text-lg text-stone-600 mb-8">
              {t('about.subtitle')}
            </p>

            {/* Story */}
            <div className="mb-10">
              <h3 className="font-outfit text-xl font-semibold text-stone-900 mb-3">
                {t('about.story.title')}
              </h3>
              <p className="text-stone-600 leading-relaxed">
                {t('about.story.content')}
              </p>
            </div>

            {/* Values Grid */}
            <div>
              <h3 className="font-outfit text-xl font-semibold text-stone-900 mb-6">
                {t('about.values.title')}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {values.map((value, index) => (
                  <motion.div
                    key={value.key}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 * index }}
                    className="bg-white rounded-xl p-4 border border-stone-100 hover:shadow-md transition-shadow"
                    data-testid={`about-value-${value.key}`}
                  >
                    <div className={`w-10 h-10 ${value.color} rounded-lg flex items-center justify-center mb-3`}>
                      <value.icon className="w-5 h-5" />
                    </div>
                    <h4 className="font-semibold text-stone-900 mb-1">
                      {t(`about.values.${value.key}.title`)}
                    </h4>
                    <p className="text-sm text-stone-500">
                      {t(`about.values.${value.key}.description`)}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Certifications */}
            {/*<div className="mt-10">
              <h3 className="font-outfit text-lg font-semibold text-stone-900 mb-4">
                {t('about.certifications')}
              </h3>
              <div className="flex flex-wrap gap-3">
                {certifications.map((cert, index) => (
                  <span
                    key={index}
                    className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium"
                  >
                    {cert}
                  </span>
                ))}
              </div>
            </div>*/}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
